/**
 * WarningRow — renders one i18n-friendly ExecutionWarning (sl-g3on.5.2).
 *
 * sl-g3on.5.3: The backend never emits free-form English text — only an
 * i18n key plus a structured params bag. This component:
 *   1. Resolves field IDs in the params to human-readable names against
 *      the workflow definition cached in Y.js (lookupFieldName helper).
 *   2. Calls the i18n proxy with the (possibly resolved) params.
 *   3. Surfaces the raw params bag in a collapsed `<details>` for power
 *      users / debugging.
 *
 * Backwards compatible with the historical string-shaped warnings emitted by
 * the input-coverage path (sl-i6t9): those reach us wrapped in
 * `{ key: '...input-coverage-incomplete', params: { message } }`.
 */

import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import type { FC } from 'react';

import type { ExecutionWarning } from '../../services/execution';
import type { WorkflowSchema } from '../../services/workflow';
import { lookupFieldName } from '../utils/lookup-field-name';

interface WarningRowProps {
  warning: ExecutionWarning;
  workflowSchema: WorkflowSchema | null;
}

export const WarningRow: FC<WarningRowProps> = ({
  warning,
  workflowSchema,
}) => {
  const t = useI18n();

  // Resolve field IDs to human-readable names. The lookup is permissive:
  // when the ID is unknown the rendered placeholder falls back to the
  // raw ID so the user can still copy it for debugging.
  const targetFieldId = stringParam(warning.params.targetFieldId);
  const sourceFieldId = stringParam(warning.params.sourceFieldId);
  const sourceStepId = stringParam(warning.params.sourceStepId);
  const targetFieldName =
    lookupFieldName(workflowSchema, targetFieldId) ?? targetFieldId ?? '';
  const sourceFieldName =
    lookupFieldName(workflowSchema, sourceFieldId) ?? sourceFieldId ?? '';

  // Build the interpolation bag the i18n string consumes. Numeric and
  // null params are passed through verbatim; the i18n proxy stringifies
  // them as needed.
  const interpolation: Record<string, string | number> = {
    ...stringifyForI18n(warning.params),
    targetFieldName,
    sourceFieldName,
    sourceStepId: sourceStepId ?? '',
  };

  // The i18n proxy returns a function for any key — calling it with no
  // args returns the raw value, calling it with params interpolates.
  const message = (t as Record<string, (args?: unknown) => string>)[
    warning.key
  ](interpolation);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '8px 12px',
        backgroundColor: cssVarV2('layer/background/secondary'),
        borderLeft: `3px solid ${cssVarV2('block/callout/icon/orange')}`,
        borderRadius: '6px',
        fontSize: '12px',
        color: cssVarV2('text/primary'),
      }}
      data-testid="warning-row"
    >
      <span>{message}</span>
      <details style={{ fontSize: '11px', color: cssVarV2('text/tertiary') }}>
        <summary style={{ cursor: 'pointer' }}>
          {t['com.streamlain.streamlain.common.details']?.() ?? 'Details'}
        </summary>
        <pre
          style={{
            margin: '4px 0 0',
            padding: '6px',
            background: cssVarV2('layer/background/primary'),
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {JSON.stringify(warning.params, null, 2)}
        </pre>
      </details>
    </div>
  );
};

function stringParam(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

function stringifyForI18n(
  params: Readonly<Record<string, string | number | null>>
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === null) {
      out[k] = '';
    } else {
      out[k] = v;
    }
  }
  return out;
}
